import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';

import type DataSelect from 'webapp/models-custom/data-select';

interface Args {
	model: DataSelect;
	isOpen: boolean;
	toggle: () => void;
	changeTitle: (value: string) => void;
}

interface BlockSettingsMetricTitleComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class BlockSettingsMetricTitleComponent extends Component<
	BlockSettingsMetricTitleComponentInterface<Args>
> {
	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.titleToEdit = this.args.model.shownTitle;
	}

	// ----- TITLE EDIT START -----

	isEditing = false;
	@tracked titleToEdit; // we only want to save on focusOut to avoid rerendering block content

	@action
	startEdit(e: FocusEvent): void {
		e.stopPropagation();
		this.isEditing = true;
	}

	@action
	endEdit(): void {
		const title = this.titleToEdit.trim();
		this.args.changeTitle(title);
		this.isEditing = false;
	}

	@action
	updateTitleToEdit() {
		if (!this.isEditing) {
			const shownTitle = this.args.model.shownTitle;
			if (this.titleToEdit !== shownTitle) {
				this.titleToEdit = shownTitle;
			}
		}
	}

	// ----- TITLE EDIT END -----

	@action
	toggle(e: PointerEvent) {
		const target = e.target as HTMLElement;
		if (this.args.isOpen) {
			// only click on the toggler can close the section
			if (target?.closest('.bs__metric__toggler')) {
				this.args.toggle();
			}
		} else {
			this.args.toggle();
		}
	}
	<template>
		<div
			class="bs__metric__title"
			role="button"
			{{on "click" this.toggle}}
			{{didUpdate this.updateTitleToEdit @model.shownTitle}}
		>
			{{#if @isOpen}}
				<FormInputComponent
					name="ds-title-{{@model.id}}"
					maxlength="50"
					@value={{this.titleToEdit}}
					@focusIn={{this.startEdit}}
					@focusOut={{this.endEdit}}
					@loseFocusOnEnter={{true}}
					@theme="dark"
				/>
			{{else}}
				<span class="title-text">
					{{@model.shownTitle}}
				</span>
			{{/if}}

			{{yield}}

		</div>
	</template>
}
